import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  DialogContentText,
  AlertColor,
  Link,
  CircularProgress,
  Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { postRequest } from 'utils/helper';
import { ToastSnackBar } from '../Setting/ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  box: {
    display: 'flex',
    alignItems: 'center',
    padding: '30px 0px 60px 50px'
  },
  dialogPaper: {
    minWidth: theme.spacing(45),
    minHeight: theme.spacing(30)
  },
  requestContent: {
    marginBottom: theme.spacing(2.5)
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  },
  cancelButton: {
    color: theme.palette.text.primary
  },
  emailBold: {
    display: 'contents'
  },
  sendingRequest: {
    paddingLeft: theme.spacing(1)
  },
  subtext: {
    paddingTop: theme.spacing(2.5)
  },
  successIcon: {
    fontSize: theme.spacing(9)
  },
  successText: {
    display: 'flex'
  },
  textPadding: {
    paddingBottom: '5%'
  },
  okButton: {
    width: theme.spacing(12)
  }
}));

type RequestConnectionDialogProps = {
  companyKey: string;
  shortName: string;
  open: boolean;
  handleRequestDialogClose: () => void;
};

type RequestSuccessProps = {
  emailId: string;
  handleRequestDialogClose: () => void;
};

const RequestSuccess: React.FC<RequestSuccessProps> = ({
  emailId,
  handleRequestDialogClose
}: RequestSuccessProps) => {
  const classes = useStyles();
  return (
    <>
      <DialogContent
        className={classes.textPadding}
        data-testid="request-success-content"
      >
        <DialogContentText
          className={classes.successText}
          data-testid="request-success-content-text"
        >
          <CheckCircleIcon
            color="success"
            className={classes.successIcon}
            data-testid="success-icon"
          />
          <FormattedMessage
            id={'notImplementedConnectors.requestSuccess.thankYou'}
          />
          <br />
          <FormattedMessage
            id="notImplementedConnectors.requestSuccess.successMessage"
            values={{
              email: <span className={classes.emailBold}>{emailId}</span>
            }}
          />
        </DialogContentText>
        <Typography variant="body2" data-testid="contact-link-message">
          <FormattedMessage id="notImplementedConnectors.requestSuccess.contactMessage" />
          <Link
            href="https://www.replicon.com/customerzone/contact-support/"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            <FormattedMessage id="notImplementedConnectors.requestSuccess.deltekSupport" />
          </Link>
          <FormattedMessage id="notImplementedConnectors.requestSuccess.anyQuestions" />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleRequestDialogClose}
          color="primary"
          autoFocus
          variant="contained"
          className={classes.okButton}
          data-testid="okButton"
        >
          <FormattedMessage id="notImplementedConnectors.requestSuccess.okButton" />
        </Button>
      </DialogActions>
    </>
  );
};

export const RequestConnectionDialog: React.FC<RequestConnectionDialogProps> = ({
  companyKey,
  shortName,
  open,
  handleRequestDialogClose: handleEmailDialogClose
}: RequestConnectionDialogProps) => {
  const [emailText, setEmailText] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequestSuccess, setIsRequestSuccess] = useState<boolean>(false);

  const classes = useStyles();
  const { formatMessage } = useIntl();

  const handleChange = useCallback((event: any) => {
    const value = event.target.value;

    setEmailText(value);
  }, []);

  const onSnackbarClose = () => {
    setIsSnackBarOpen(false);
  };
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  const phoneHandleChange = (event: any) => {
    let value = event.target.value;
    value = value.replace(/[^\d+\-() \s]/g, '');
    setPhoneNumber(value);
  };

  const handleApiCall = useCallback(async () => {
    try {
      const body = {
        connectorName: shortName,
        companyKey: companyKey,
        emailId: emailText,
        phoneNumber: phoneNumber
      };
      const res = await postRequest(
        '/integration-settings-api/request-connector-email',
        body,
        true
      );
      if (res.data) {
        setIsLoading(false);
        setIsRequestSuccess(true);
      }
    } catch (error: any) {
      setSeverity('error');
      setIsLoading(false);
      onSnackbarOpen();
    }
  }, [companyKey, shortName, emailText, onSnackbarOpen, phoneNumber]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    await handleApiCall();
  };
  const alertMessage = useMemo(() => {
    if (severity === 'error') {
      return formatMessage({
        id: 'notImplementedConnectors.retryMessage'
      });
    }
  }, [formatMessage, severity]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleEmailDialogClose}
        PaperProps={{ className: classes.dialogPaper }}
        data-testid="requestDialogContent"
      >
        <DialogTitle data-testid="request-connector-title">
          <FormattedMessage
            id={'notImplementedConnectors.requestPage.dialogTitle'}
          />
        </DialogTitle>
        <DialogContent data-testid="request-content">
          {isRequestSuccess ? (
            <RequestSuccess
              emailId={emailText}
              handleRequestDialogClose={handleEmailDialogClose}
            />
          ) : (
            <form data-testid="form-render" onSubmit={handleSubmit}>
              {isLoading ? (
                <Box className={classes.box}>
                  <CircularProgress
                    size={30}
                    color="primary"
                    data-testid="render-circular-progress"
                  />
                  <Typography
                    variant="body1"
                    className={classes.sendingRequest}
                    data-testid="loading-text"
                  >
                    <FormattedMessage
                      id={'notImplementedConnectors.requestPage.sendingRequest'}
                    />
                  </Typography>
                </Box>
              ) : (
                <div>
                  <DialogContentText
                    data-testid="RequestDialogContentText"
                    className={classes.requestContent}
                  >
                    <FormattedMessage
                      id={
                        'notImplementedConnectors.requestPage.dialogContentText'
                      }
                    />
                  </DialogContentText>
                  <TextField
                    id="Business Email"
                    variant="outlined"
                    autoFocus
                    fullWidth
                    required
                    value={emailText}
                    name="email"
                    onChange={handleChange}
                    type={'email'}
                    label={
                      <FormattedMessage
                        id={'notImplementedConnectors.requestPage.emailLabel'}
                      />
                    }
                    InputLabelProps={{
                      shrink: true
                    }}
                    className={classes.textField}
                    inputProps={{
                      'data-testid': 'emailFieldSection',
                      maxLength: 100
                    }}
                  />
                  <br />
                  <TextField
                    id="Phone Number"
                    value={phoneNumber}
                    onChange={phoneHandleChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Business Phone"
                    inputProps={{
                      'data-testid': 'phoneNumberInput',
                      maxLength: 15
                    }}
                  />
                  <br />
                  <Typography
                    variant="body2"
                    className={classes.subtext}
                    data-testid="render-subtext"
                  >
                    <FormattedMessage
                      id={'notImplementedConnectors.requestPage.learnMore'}
                    />{' '}
                    <Link
                      href="https://www.deltek.com/en/legal"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      <FormattedMessage
                        id={'notImplementedConnectors.requestPage.privacy'}
                      />
                    </Link>
                    .
                  </Typography>
                  <br />
                </div>
              )}
              <DialogActions>
                <Button
                  onClick={handleEmailDialogClose}
                  color="grey"
                  autoFocus
                  variant="contained"
                  data-testid="cancelButton"
                >
                  <FormattedMessage
                    id={'notImplementedConnectors.requestPage.cancelButton'}
                  />
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  data-testid="requestButton"
                >
                  <FormattedMessage
                    id={'notImplementedConnectors.requestPage.requestButton'}
                  />
                </Button>
              </DialogActions>
            </form>
          )}
        </DialogContent>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={severity}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </React.Fragment>
  );
};
